import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    Radar, Search, RefreshCw, Phone, MessageSquare, MapPin,
    TrendingUp, Clock, ToggleLeft, ToggleRight, Zap, Plus,
    DollarSign, Home, User, Mail, ExternalLink, Crosshair,
    CheckCircle, Flame, Thermometer, Snowflake
} from 'lucide-react';

const CATEGORY_STYLE = {
    HOT: { bg: 'bg-red-50 border-red-200', badge: 'bg-red-500 text-white', icon: Flame },
    WARM: { bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-500 text-white', icon: Thermometer },
    COLD: { bg: 'bg-blue-50 border-blue-200', badge: 'bg-blue-400 text-white', icon: Snowflake },
};

export function LeadRadarTab() {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState(null);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scanning, setScanning] = useState(false);
    const [markets, setMarkets] = useState([]);
    const [newMarket, setNewMarket] = useState({ city: '', state: '' });

    const loadData = useCallback(async () => {
        try {
            const [feedRes, statsRes, configRes] = await Promise.all([
                API.get('/opportunities/feed?category=all'),
                API.get('/opportunities/stats/summary'),
                API.get('/opportunities/autopilot/config'),
            ]);
            // Show only skip-traced leads with phone numbers first, then others
            const opps = feedRes.data.opportunities || [];
            const withPhone = opps.filter(o => o.contact_phone);
            const withoutPhone = opps.filter(o => !o.contact_phone);
            setLeads([...withPhone, ...withoutPhone]);
            setStats(statsRes.data);
            setConfig(configRes.data);
            setMarkets(configRes.data?.markets || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const runAutopilot = async (market, state) => {
        setScanning(true);
        try {
            await API.post('/opportunities/autopilot/run', { market: market || '', state: state || '' });
            // Poll for results
            let polls = 0;
            const interval = setInterval(async () => {
                polls++;
                await loadData();
                if (polls >= 8) { clearInterval(interval); setScanning(false); }
            }, 5000);
            setTimeout(() => { clearInterval(interval); setScanning(false); }, 45000);
        } catch (err) { alert(err.response?.data?.detail || 'Failed'); setScanning(false); }
    };

    const toggleAutopilot = async () => {
        try {
            await API.post('/opportunities/autopilot/toggle', { enabled: !config?.enabled });
            await loadData();
        } catch (err) { console.error(err); }
    };

    const addMarket = async () => {
        if (!newMarket.city || !newMarket.state) return;
        const updated = [...markets, { city: newMarket.city, state: newMarket.state.toUpperCase() }];
        try {
            await API.post('/opportunities/autopilot/markets', { markets: updated });
            setMarkets(updated);
            setNewMarket({ city: '', state: '' });
        } catch (err) { console.error(err); }
    };

    const removeMarket = (idx) => {
        const updated = markets.filter((_, i) => i !== idx);
        API.post('/opportunities/autopilot/markets', { markets: updated }).then(() => setMarkets(updated));
    };

    const callLead = async (phone, name) => {
        try {
            await API.post('/signalwire/call', { to: phone, contact_name: name || 'Unknown' });
            alert('Call initiated!');
        } catch (err) { alert(err.response?.data?.detail || 'Call failed'); }
    };

    if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-zinc-400" /></div>;

    const leadsWithPhone = leads.filter(l => l.contact_phone);

    return (
        <div className="space-y-5" data-testid="lead-radar-tab">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2 text-xs text-zinc-500">
                        {config?.last_run && (
                            <span>Last run: {new Date(config.last_run).toLocaleString()} | {config.last_count || 0} new, {config.skip_trace_count || 0} traced</span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={toggleAutopilot}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${config?.enabled !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}
                        data-testid="autopilot-toggle">
                        {config?.enabled !== false ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                        Autopilot {config?.enabled !== false ? 'ON' : 'OFF'}
                    </button>
                    <Button onClick={() => runAutopilot('', '')} disabled={scanning}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white text-xs" data-testid="radar-search-btn">
                        {scanning ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Radar className="w-3.5 h-3.5 mr-1.5" />}
                        {scanning ? 'Pulling leads...' : 'Pull Leads Now'}
                    </Button>
                </div>
            </div>

            {/* Stats */}
            {stats && (
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                    <MiniStat label="Total Properties" value={stats.total} />
                    <MiniStat label="HOT" value={stats.hot} color="red" />
                    <MiniStat label="WARM" value={stats.warm} color="amber" />
                    <MiniStat label="Skip Traced" value={stats.skip_traced} color="violet" />
                    <MiniStat label="Contacted" value={stats.contacted} color="emerald" />
                    <MiniStat label="With Phone" value={leadsWithPhone.length} color="blue" />
                </div>
            )}

            {/* Markets */}
            <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-zinc-500 font-medium">Markets:</span>
                {markets.map((m, i) => (
                    <Badge key={i} className="bg-zinc-100 text-zinc-700 text-xs px-2 py-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {m.city}, {m.state}
                        <button onClick={() => removeMarket(i)} className="text-zinc-400 hover:text-red-500 ml-1">&times;</button>
                    </Badge>
                ))}
                <div className="flex items-center gap-1">
                    <Input placeholder="City" value={newMarket.city} onChange={e => setNewMarket(m => ({ ...m, city: e.target.value }))}
                        className="h-6 w-24 text-[11px]" />
                    <Input placeholder="ST" value={newMarket.state} maxLength={2}
                        onChange={e => setNewMarket(m => ({ ...m, state: e.target.value.toUpperCase() }))}
                        className="h-6 w-10 text-[11px]" />
                    <Button size="sm" onClick={addMarket} className="h-6 px-2 text-[10px] bg-zinc-800 text-white">
                        <Plus className="w-3 h-3" />
                    </Button>
                </div>
            </div>

            {/* Lead Cards — Real Properties */}
            {leads.length === 0 ? (
                <div className="bg-white rounded-2xl border border-zinc-200 p-12 text-center">
                    <Radar className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-zinc-900 font-['Outfit'] mb-2">No Leads Yet</h3>
                    <p className="text-sm text-zinc-500 mb-4">Click "Pull Leads Now" to find motivated sellers with properties 60+ days on market.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {leads.slice(0, 30).map(lead => {
                        const style = CATEGORY_STYLE[lead.category] || CATEGORY_STYLE.COLD;
                        const Icon = style.icon;
                        return (
                            <div key={lead.id} className={`${style.bg} border rounded-xl px-4 py-3`} data-testid={`lead-${lead.id}`}>
                                <div className="flex items-center gap-3">
                                    {/* Score */}
                                    <div className={`${style.badge} w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold shrink-0`}>
                                        {lead.motivation_score}
                                    </div>

                                    {/* Property info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <Badge className={`${style.badge} text-[10px] px-1.5`}>
                                                <Icon className="w-2.5 h-2.5 mr-0.5" />{lead.category}
                                            </Badge>
                                            <span className="text-[10px] text-zinc-500">{lead.daysOnMarket} days on market</span>
                                            {lead.skip_traced && <Crosshair className="w-3 h-3 text-violet-500" />}
                                        </div>
                                        <div className="text-sm font-medium text-zinc-900 truncate">{lead.address}</div>
                                        <div className="text-xs text-zinc-500">{lead.city}, {lead.state} {lead.zip} | ${(lead.price || 0).toLocaleString()} | {lead.bedrooms || '?'}bd/{lead.bathrooms || '?'}ba | {(lead.sqft || 0).toLocaleString()} sqft</div>
                                    </div>

                                    {/* Contact / Actions */}
                                    <div className="shrink-0 text-right space-y-1">
                                        {lead.contact_phone ? (
                                            <div className="flex items-center gap-1.5">
                                                <div className="text-xs">
                                                    <div className="font-medium text-zinc-800">{lead.contact_name || 'Owner'}</div>
                                                    <div className="text-zinc-500">{lead.contact_phone}</div>
                                                </div>
                                                <Button size="sm" onClick={() => callLead(lead.contact_phone, lead.contact_name)}
                                                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] h-7 px-2" data-testid={`call-${lead.id}`}>
                                                    <Phone className="w-3 h-3 mr-0.5" /> Call
                                                </Button>
                                            </div>
                                        ) : lead.skip_traced ? (
                                            <span className="text-[10px] text-zinc-400">No phone found</span>
                                        ) : (
                                            <Badge className="bg-zinc-100 text-zinc-500 text-[10px]">Pending trace</Badge>
                                        )}
                                        {lead.contact_email && (
                                            <div className="text-[10px] text-zinc-500 flex items-center gap-1">
                                                <Mail className="w-3 h-3" /> {lead.contact_email}
                                            </div>
                                        )}
                                        {lead.phones_found && lead.phones_found.length > 1 && (
                                            <div className="text-[10px] text-zinc-400">+{lead.phones_found.length - 1} more numbers</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function MiniStat({ label, value, color = 'zinc' }) {
    const colors = {
        zinc: 'text-zinc-900', red: 'text-red-600', amber: 'text-amber-600',
        violet: 'text-violet-600', emerald: 'text-emerald-600', blue: 'text-blue-600',
    };
    return (
        <div className="bg-white rounded-lg border border-zinc-100 px-3 py-2 text-center">
            <div className={`text-lg font-bold font-['Outfit'] ${colors[color]}`}>{value}</div>
            <div className="text-[10px] text-zinc-400">{label}</div>
        </div>
    );
}
